
import { v4 as uuidv4 } from 'uuid';
import { 
  doc, 
  collection, 
  setDoc, 
  addDoc, 
  updateDoc, 
  arrayUnion, 
  Timestamp,
  getDoc,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { 
  PORTFOLIOS_COLLECTION, 
  BUSINESS_COLLECTION
} from './constants';
import { 
  Lead, 
  LeadType, 
  LeadStatus, 
  LeadOutcome 
} from '@/components/business/leads/types/LeadTypes';
import { format, subDays, subHours, subMinutes } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

/**
 * Generate a fake plumbing business and add it to a portfolio in Firestore
 */
export const createFakePlumbingBusiness = async () => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.error("No authenticated user found");
      return { success: false, error: "You must be signed in to create a business" };
    }
    
    // Create portfolio first
    const portfolioId = uuidv4();
    const portfolioName = "Plumbing Services Portfolio";
    
    // Create business data
    const businessId = uuidv4();
    const businessData = {
      id: businessId,
      name: "Smith Family Plumbing",
      shortName: "Smith Plumbing",
      displayName: "Smith Family Plumbing",
      phone: "(555) 123-4567",
      email: "info@smithplumbing.example.com",
      website: "https://www.smithplumbing.example.com",
      industry: "Home Services",
      industryVertical: "Plumbing",
      address: "123 Main Street",
      city: "Sacramento",
      state: "CA",
      zip: "95814",
      founded: "2010-05-15",
      timeZone: "America/Los_Angeles",
      status: "active",
      coordinates: { lat: 38.5816, lng: -121.4944 },
      createdAt: new Date(),
      updatedAt: new Date(),
      portfolioId: portfolioId,
      // Add monthly budget
      monthlyBudget: 4000
    };
    
    // Create member data for the current user
    const memberData = {
      userId: currentUser.uid,
      role: "Primary Owner",
      permissions: [
        { id: 'campaigns', name: 'Campaigns', checked: true },
        { id: 'budget', name: 'Budget', checked: true },
        { id: 'billing', name: 'Billing', checked: true },
        { id: 'leads', name: 'Leads', checked: true },
        { id: 'businessUsers', name: 'Business Users', checked: true },
        { id: 'portfolioUsers', name: 'Portfolio Users', checked: true }
      ],
      status: 'active',
      email: currentUser.email,
      firstName: currentUser.displayName?.split(' ')[0] || '',
      lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
      invitedAt: new Date(),
      joinedAt: new Date()
    };
    
    // Create portfolio in Firestore with regular timestamps
    await setDoc(doc(db, PORTFOLIOS_COLLECTION, portfolioId), {
      id: portfolioId,
      name: portfolioName,
      owner: currentUser.uid,
      businesses: [businessId],
      createdAt: new Date(),
      updatedAt: new Date(),
      members: [memberData]
    });
    
    console.log(`Created portfolio ${portfolioId} with name ${portfolioName}`);
    
    // Create business in Firestore
    await setDoc(doc(db, BUSINESS_COLLECTION, businessId), {
      ...businessData,
      members: [memberData]
    });
    
    console.log(`Created business ${businessId} with name ${businessData.name}`);
    
    // Update user document to add portfolio and business references
    const userRef = doc(db, 'users', currentUser.uid);
    
    // Check if user document exists
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // Update existing user document
      await updateDoc(userRef, {
        portfolios: arrayUnion(portfolioId),
        businesses: arrayUnion(businessId),
        updatedAt: new Date()
      });
    } else {
      // Create new user document
      await setDoc(userRef, {
        id: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
        portfolios: [portfolioId],
        businesses: [businessId],
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    console.log(`Updated user ${currentUser.uid} with portfolio and business references`);
    
    // Generate fake leads for the business - changed from 150 to 50
    await generateFakePlumbingLeads(businessId, 50);
    
    return { 
      success: true, 
      portfolioId, 
      businessId, 
      message: "Created fake plumbing business with leads successfully" 
    };
  } catch (error: any) {
    console.error("Error creating fake plumbing business:", error);
    return { 
      success: false, 
      error: error.message || "Failed to create fake plumbing business" 
    };
  }
};

/**
 * Generate a set of fake plumbing leads for a business
 */
export const generateFakePlumbingLeads = async (businessId: string, count: number = 150) => {
  try {
    const now = new Date();
    
    // Plumbing-specific lead data arrays
    const plumbingServices = [
      "Water Heater Installation",
      "Drain Cleaning",
      "Pipe Repair",
      "Toilet Replacement",
      "Leak Detection",
      "Faucet Installation",
      "Sewer Line Repair",
      "Garbage Disposal Installation",
      "Water Softener Installation",
      "Bathroom Remodeling",
      "Kitchen Sink Repair"
    ];
    
    const firstNames = ['John', 'Sarah', 'Michael', 'Jessica', 'David', 'Emily', 'Robert', 'Lisa', 'Thomas', 'Amanda', 'James', 'Jennifer', 'William', 'Elizabeth', 'Richard'];
    const lastNames = ['Smith', 'Johnson', 'Brown', 'Wilson', 'Lee', 'Davis', 'Miller', 'Anderson', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Garcia', 'Martinez'];
    
    // Distribution of leads over time
    const timeDistribution = [
      { period: 7, percentage: 0.2 },    // 20% in last 7 days
      { period: 30, percentage: 0.3 },   // 30% in last 8-30 days
      { period: 90, percentage: 0.3 },   // 30% in last 31-90 days
      { period: 365, percentage: 0.2 }   // 20% in last 91-365 days
    ];
    
    // Calculate counts for each period
    const periodCounts = timeDistribution.map(period => {
      return {
        period: period.period,
        count: Math.floor(count * period.percentage)
      };
    });
    
    // Add any remainder to the last period due to rounding
    const totalAssigned = periodCounts.reduce((sum, period) => sum + period.count, 0);
    if (totalAssigned < count) {
      periodCounts[periodCounts.length - 1].count += (count - totalAssigned);
    }
    
    let leads: any[] = [];
    
    // Generate leads for each time period
    for (const period of periodCounts) {
      const periodStartDays = period === periodCounts[0] ? 0 : 
        timeDistribution[periodCounts.indexOf(period) - 1].period;
      const periodEndDays = period.period;
      
      for (let i = 0; i < period.count; i++) {
        // Generate random date within the specified range
        const daysAgo = periodStartDays + Math.floor(Math.random() * (periodEndDays - periodStartDays));
        const randomDate = subDays(now, daysAgo);
        
        // Add random hours and minutes
        const hoursAgo = Math.floor(Math.random() * 24);
        const minutesAgo = Math.floor(Math.random() * 60);
        const leadDate = subMinutes(subHours(randomDate, hoursAgo), minutesAgo);
        
        // Format date with time as MM/DD/YYYY hh:mm am/pm
        const formattedDate = format(leadDate, 'MM/dd/yyyy hh:mm a');
        
        // Generate lead type with 60% calls, 40% forms
        const leadType: LeadType = Math.random() > 0.4 ? 'Call' : 'Form';
        
        // Generate name
        const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
        const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
        const fullName = `${firstName} ${lastName}`;
        
        // Generate phone
        const areaCode = Math.floor(Math.random() * 800) + 200;
        const secondPart = Math.floor(Math.random() * 900) + 100;
        const thirdPart = Math.floor(Math.random() * 9000) + 1000;
        const phone = `(${areaCode}) ${secondPart}-${thirdPart}`;
        
        // Generate status based on type
        let status: LeadStatus;
        if (leadType === 'Call') {
          const callStatuses: LeadStatus[] = ['Call Answered', 'Call Missed', 'Call Returned'];
          status = callStatuses[Math.floor(Math.random() * callStatuses.length)];
        } else {
          const formStatuses: LeadStatus[] = ['Form Received', 'Form Replied'];
          status = formStatuses[Math.floor(Math.random() * formStatuses.length)];
        }
        
        // Outcome - higher chance of sale won for more recent leads
        let outcome: LeadOutcome;
        const outcomeRoll = Math.random();
        
        if (daysAgo <= 7 && outcomeRoll < 0.45) {
          outcome = 'Sale Won'; // 45% chance of sale in last 7 days
        } else if (daysAgo <= 30 && outcomeRoll < 0.35) {
          outcome = 'Sale Won'; // 35% chance of sale in last 30 days
        } else if (daysAgo <= 90 && outcomeRoll < 0.25) {
          outcome = 'Sale Won'; // 25% chance of sale in last 90 days
        } else if (outcomeRoll < 0.15) {
          outcome = 'Sale Won'; // 15% chance of sale otherwise
        } else {
          // Other outcomes
          const otherOutcomes: LeadOutcome[] = [
            'Appointment Set',
            'Call Back Requested',
            'They Will Call Back',
            'Lost (job size/pricing)',
            'Lost (service timeline)',
            'Lost (service not provided)',
            'Unknown',
            'Existing Customer'
          ];
          outcome = otherOutcomes[Math.floor(Math.random() * otherOutcomes.length)];
        }
        
        // Generate value - only for won sales, between $150 and $3500
        let value = '';
        if (outcome === 'Sale Won') {
          // Plumbing services tend to cost between $150 and $3500
          const amount = Math.floor(Math.random() * 3350) + 150;
          value = `$${amount}`;
        }
        
        // Calculate timestamp for Firestore
        const firestoreTimestamp = Timestamp.fromDate(leadDate);
        
        // Generate lead data
        const leadData: any = {
          id: uuidv4(),
          name: fullName,
          phone: phone,
          status: status,
          outcome: outcome,
          value: value,
          date: formattedDate,
          timestamp: firestoreTimestamp,
          type: leadType
        };
        
        // Add type-specific data
        if (leadType === 'Call') {
          // Generate duration between 30 seconds and 8 minutes
          const minutes = Math.floor(Math.random() * 8);
          const seconds = Math.floor(Math.random() * 60);
          leadData.audioDuration = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
          
          // Generate AI summary
          const service = plumbingServices[Math.floor(Math.random() * plumbingServices.length)];
          leadData.aiSummary = `${fullName} called about ${service}. ${
            Math.random() > 0.7 ? 'They mentioned it was urgent. ' : ''
          }${Math.random() > 0.5 ? 'They asked about pricing. ' : ''}`;
        } else {
          // Form fields
          leadData.formFields = {
            message: `I need a plumber for ${plumbingServices[Math.floor(Math.random() * plumbingServices.length)]}. ${
              Math.random() > 0.5 ? 'Please call me ASAP. ' : 'When can you come out? '
            }`,
            zipCode: `${Math.floor(Math.random() * 90000) + 10000}`,
            email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`
          };
        }
        
        leads.push(leadData);
      }
    }
    
    console.log(`Generated ${leads.length} fake plumbing leads`);
    
    // Store leads in Firestore using the new path structure
    const leadsCollectionRef = collection(db, BUSINESS_COLLECTION, businessId, 'leads');
    
    // Use batched writes for better performance
    const batchSize = 20;
    for (let i = 0; i < leads.length; i += batchSize) {
      const batch = leads.slice(i, i + batchSize);
      const promises = batch.map(lead => addDoc(leadsCollectionRef, lead));
      await Promise.all(promises);
    }
    
    console.log(`Successfully added ${leads.length} leads to Firestore for business ${businessId}`);
    return { success: true, count: leads.length };
  } catch (error: any) {
    console.error("Error generating fake plumbing leads:", error);
    return { success: false, error: error.message };
  }
};
